import { currencyFormatter } from "@/utils";
import {
  BarcodeConfig,
  barcodeConfigManager,
  OrientationEnum,
} from "../config/barcode";
import JsBarcode from "jsbarcode";
import { color, appColors } from "@/constants/color";

const generateBarcodeBase64 = (value: string) => {
  const canvas = document.createElement("canvas");
  try {
    JsBarcode(canvas, value, {
      format: "CODE128",
      displayValue: true,
      fontOptions: "bold",
      font: "Arial",
      fontSize: 26,
      textMargin: 2,
      margin: 0,
      height: 45,
      background: "#FFFFFF",
      lineColor: "#000000",
    });
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Failed to generate barcode:", error);
    return "";
  }
};
export const formatBarcodePrice = (amount: number | null | undefined): string => {
  if (amount == null) return "₹0";
  const num = Number(amount) || 0;
  if (Math.abs(num) >= 1000) {
    // For amounts >= 1000, remove .00 to keep barcode label clean and compact
    return Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  }
  return currencyFormatter(num);
};

export function calculateDynamicPriceFontSize(
  finalConfig: BarcodeConfig,
  formattedMrp: string,
  formattedSellingPrice: string,
  basePriceFontSize: number
): number {
  if (!finalConfig.includeMrp && !finalConfig.includeSellingPrice) return basePriceFontSize;

  const stickerWidth = finalConfig.width || 40;
  const pxWidth = stickerWidth * 3.78;

  let totalChars = 0;
  if (finalConfig.includeMrp) totalChars += formattedMrp.length + 5;
  if (finalConfig.includeSellingPrice) totalChars += formattedSellingPrice.length + 7;
  if (finalConfig.includeMrp && finalConfig.includeSellingPrice) totalChars += 2;

  const availablePx = pxWidth - 12;
  const idealFontSize = availablePx / (totalChars * 0.55);

  return Number(Math.max(6.5, Math.min(basePriceFontSize, idealFontSize)).toFixed(1));
}

export function calculateDynamicBarcodeDimensions(finalConfig: BarcodeConfig) {
  const stickerWidth = finalConfig.width || 40;
  const stickerHeight = finalConfig.height || 22;

  // 1. Percentage-based vertical heights
  // Top safety padding: 12% of sticker height (e.g. 2.6mm on 22mm, 3.0mm on 25mm)
  const topPaddingMm = Math.max(1.8, Number((stickerHeight * 0.12).toFixed(1)));
  
  // Barcode height: 32% of sticker height (min 5.0mm)
  const barcodeHeightMm = Math.max(5.0, Number((stickerHeight * 0.32).toFixed(1)));
  
  // Barcode width: 88% of sticker width (min 20mm, max width - 3mm)
  const barcodeWidthMm = Math.min(stickerWidth - 3, Math.max(20, Number((stickerWidth * 0.88).toFixed(1))));

  // Logo height: 13% of sticker height (min 2.5mm)
  const logoHeightMm = Math.max(2.5, Number((stickerHeight * 0.13).toFixed(1)));

  // Element gaps: 1.5% of sticker height (min 0.2mm)
  const elementGap = `${Math.max(0.2, Number((stickerHeight * 0.015).toFixed(1)))}mm`;

  // 2. Font sizes (in px, converted from mm equivalent: 1mm ≈ 3.78px)
  const pxHeight = stickerHeight * 3.78;
  const pxWidth = stickerWidth * 3.78;

  // Store Name: 13% of height (min 9px)
  const storeNameFontSize = Math.max(9, Number((pxHeight * 0.13).toFixed(1)));

  // Product Name: 11.5% of height (min 8px)
  const productNameFontSize = Math.max(8, Number((pxHeight * 0.115).toFixed(1)));

  // Price / MRP: 
  // If both are displayed in single line: width-constrained so they NEVER wrap or overflow
  const priceFontSize = (finalConfig.includeMrp && finalConfig.includeSellingPrice)
    ? Math.max(7.5, Number(Math.min(pxHeight * 0.11, pxWidth / 17.5).toFixed(1)))
    : Math.max(8.5, Number((pxHeight * 0.12).toFixed(1)));

  // Product Code: 11.5% of height (min 8.5px)
  const productCodeFontSize = Math.max(8.5, Number((pxHeight * 0.115).toFixed(1)));

  // Max characters allowed in 1 line based on width
  const maxChars = Math.max(16, Math.round(stickerWidth / 1.2));

  return {
    barcodeHeightMm,
    barcodeWidthMm,
    storeNameFontSize,
    productNameFontSize,
    priceFontSize,
    productCodeFontSize,
    topPaddingMm,
    elementGap,
    maxChars,
    logoHeightMm,
  };
}

export const barcodeHTML = async ({
  mrp,
  sellingPrice,
  productName, 
  barcode, 
  slug, 
  storeName, 
  productCode 
}: any) => {

  const finalConfig = barcodeConfigManager.getConfig();
  const layout = {
    gridTemplateColumns: `repeat(${finalConfig.stickersPerRow}, 1fr)`,
    columnGap: `${finalConfig.stickerGap}mm`,
    rowGap: `${finalConfig.rowGap}mm`
  };
  const pagePadding = `${finalConfig.pageMargin}mm`;
  
  const barcodeImage = generateBarcodeBase64(barcode);
  const formattedMrp = formatBarcodePrice(mrp);
  const formattedSellingPrice = formatBarcodePrice(sellingPrice);
  const isPortrait = finalConfig.orientation === OrientationEnum.Portrait;
  const {
    barcodeHeightMm,
    barcodeWidthMm,
    storeNameFontSize,
    productNameFontSize,
    priceFontSize: basePriceFontSize,
    productCodeFontSize,
    topPaddingMm,
    elementGap,
    maxChars,
    logoHeightMm,
  } = calculateDynamicBarcodeDimensions(finalConfig);

  const priceFontSize = calculateDynamicPriceFontSize(
    finalConfig,
    formattedMrp,
    formattedSellingPrice,
    basePriceFontSize
  );
  const renderBarcode = () => `
    <div style="
      width: 100%; 
      height: ${finalConfig.height}mm;
      max-height: ${finalConfig.height}mm;
      margin: 0;
      padding: ${topPaddingMm}mm 0.5mm 0.1mm 0.5mm; 
      background: white; 
      text-align: center; 
      box-sizing: border-box; 
      display: flex;
      flex-direction: column;
      justify-content: flex-start; 
      align-items: center; 
      overflow: hidden;
      ${isPortrait ? `width: calc(${finalConfig.height}mm - 0.5mm); height: calc(${finalConfig.width}mm - 0.5mm);` : ''}
    ">
      ${finalConfig.logoUrl ? `
        <img src="${finalConfig.logoUrl}" alt="logo" style="
          max-height: ${logoHeightMm}mm;
          max-width: 85%;
          object-fit: contain;
          margin-bottom: ${elementGap};
          flex-shrink: 0;
        " />
      ` : ''}

      <!-- Store Name -->
      ${finalConfig.includeStoreName ? `
      <div style="
        font-size: ${storeNameFontSize}px; 
        font-weight: 900; 
        white-space: nowrap; 
        overflow: hidden; 
        text-overflow: ellipsis; 
        width: 100%;
        margin-bottom: ${elementGap};
        line-height: 1.1;
        flex-shrink: 0;
      ">
        ${storeName?.slice(0, maxChars)}
      </div>
      ` : ''}

      <!-- Product Name -->
      ${finalConfig.includeProductName ? `
      <div style="
        font-size: ${productNameFontSize}px; 
        font-weight: bold; 
        white-space: nowrap; 
        overflow: hidden; 
        text-overflow: ellipsis; 
        width: 100%;
        margin-bottom: ${elementGap};
        line-height: 1.1;
        flex-shrink: 0;
      ">
        ${productName?.slice(0, maxChars)}
      </div>
      ` : ''}

      <!-- Barcode Image -->
      <img 
        src="${barcodeImage}" 
        alt="barcode" 
        style="
          width: ${barcodeWidthMm}mm; 
          height: ${barcodeHeightMm}mm;
          display: block; 
          font-weight: bold;
          object-fit: contain;
          margin: 0.1mm 0 ${elementGap} 0;
          flex-shrink: 0;
        " 
      />

      <div style="width: 100%; display: flex; flex-direction: row; gap: 1.2mm; align-items: center; justify-content: center; text-align: center; margin-bottom: ${elementGap}; line-height: 1; flex-shrink: 0; white-space: nowrap;">
        <!-- MRP -->
        ${finalConfig.includeMrp ? `
        <div style="
          font-size: ${priceFontSize}px; 
          font-weight: 800;
          text-align: center;
          white-space: nowrap;
          letter-spacing: -0.2px;
        ">
          MRP: ${formattedMrp}
        </div>
        ` : ''}

        <!-- Selling -->

        ${finalConfig.includeSellingPrice ? `
        <div style="
          font-size: ${priceFontSize}px; 
          font-weight: 800; 
          text-align: center;
          white-space: nowrap;
          letter-spacing: -0.2px;
        ">

          Price: ${formattedSellingPrice}
        </div>
        ` : ''}
      </div>

      



      <!-- Product Code -->
      ${(finalConfig.includeProductCode) ? `
        <div style="
          font-size: ${productCodeFontSize}px; 
          font-weight: 800;
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis;
          width: 100%;
          line-height: 1;
          flex-shrink: 0;
        ">
          ${productCode ?? ''}
        </div>
      ` : ''}
    </div>
  `;

  // Calculate how many stickers to render based on config
  const totalStickers = finalConfig.stickersPerRow * finalConfig.rowsPerPage;
  const stickersHTML = Array(totalStickers)
    .fill(0)
    .map(() => renderBarcode())
    .join("");

  return `<!DOCTYPE html>
    <html>
    <head>
      <style>
        @media print {
          @page {
            size: auto;
            margin: 0mm;
          }
          body { margin: 0; padding: 0; overflow: hidden; }
          .sticker-page { 
            page-break-after: always; 
          }
          .sticker-page:last-child {
            page-break-after: auto;
          }
        }
      </style>
    </head>
    <body style="margin:0; padding:0; height: ${finalConfig.height}mm; overflow: hidden; font-family: Arial, sans-serif;">
      <div class="sticker-page" style="
        width: 100%; 
        height: ${finalConfig.height}mm;
        max-height: ${finalConfig.height}mm;
        display: grid; 
        margin: 0 auto;
        grid-template-columns: ${layout.gridTemplateColumns};
        column-gap: ${layout.columnGap};
        row-gap: ${layout.rowGap};
        padding: ${pagePadding};
        box-sizing: border-box;
        overflow: hidden;
      ">
        ${stickersHTML}
      </div>
    </body>
    </html>`;
};

export const createCombinedBarcodeHTML = async (
  barcodes: any[],
  config?: BarcodeConfig,
) => {
  const finalConfig = barcodeConfigManager.getConfig();
  const layout = {
    gridTemplateColumns: `repeat(${finalConfig.stickersPerRow}, 1fr)`,
    columnGap: `${finalConfig.stickerGap}mm`,
    rowGap: `${finalConfig.rowGap}mm`,
  };
  const pagePadding = `${finalConfig.pageMargin}mm`;
  const isPortrait = finalConfig.orientation === OrientationEnum.Portrait;
  const {
    barcodeHeightMm,
    barcodeWidthMm,
    storeNameFontSize,
    productNameFontSize,
    priceFontSize: basePriceFontSize,
    productCodeFontSize,
    topPaddingMm,
    elementGap,
    maxChars,
    logoHeightMm,
  } = calculateDynamicBarcodeDimensions(finalConfig);
  
  const renderBarcode = (barcodeData: any) => {
    const barcodeImage = generateBarcodeBase64(barcodeData.barcode);
    const formattedMrp = formatBarcodePrice(barcodeData.mrp);
    const formattedSellingPrice = formatBarcodePrice(barcodeData.sellingPrice);
    const priceFontSize = calculateDynamicPriceFontSize(
      finalConfig,
      formattedMrp,
      formattedSellingPrice,
      basePriceFontSize
    );
    
    return `
      <div style="
        width: 100%; 
        height: ${finalConfig.height}mm;
        max-height: ${finalConfig.height}mm;
        margin: 0;
        padding: ${topPaddingMm}mm 0.5mm 0.1mm 0.5mm; 
        background: white; 
        text-align: center; 
        box-sizing: border-box; 
        display: flex; 
        flex-direction: column;
        justify-content: flex-start; 
        align-items: center; 
        overflow: hidden;
        ${isPortrait ? 'transform: rotate(90deg); transform-origin: center;' : ''}
        ${isPortrait ? `width: ${finalConfig.height}mm; height: ${finalConfig.width}mm;` : ''}
      ">
        ${finalConfig.logoUrl ? `
          <img src="${finalConfig.logoUrl}" alt="logo" style="
            max-height: ${logoHeightMm}mm;
            max-width: 85%;
            object-fit: contain;
            margin-bottom: ${elementGap};
            flex-shrink: 0;
          " />
        ` : ''}

        <!-- Store Name -->
        ${finalConfig.includeStoreName ? `
        <div style="
          font-size: ${storeNameFontSize}px; 
          font-weight: 900; 
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis; 
          width: 100%;
          margin-bottom: ${elementGap};
          line-height: 1.1;
          flex-shrink: 0;
        ">
          ${barcodeData.storeName?.slice(0, maxChars)}
        </div>
        ` : ''}

        <!-- Product Name -->
        ${finalConfig.includeProductName ? `
        <div style="
          font-size: ${productNameFontSize}px; 
          font-weight: bold; 
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis; 
          width: 100%;
          margin-bottom: ${elementGap};
          line-height: 1.1;
          flex-shrink: 0;
        ">
          ${barcodeData.productName?.slice(0, maxChars)}
        </div>
        ` : ''}

        <!-- Barcode Image -->
        <img 
          src="${barcodeImage}" 
          alt="barcode" 
          style="
            width: ${barcodeWidthMm}mm; 
            height: ${barcodeHeightMm}mm; 
            display: block; 
            font-weight: bold;
            object-fit: contain;
            margin: 0.1mm 0 ${elementGap} 0;
            flex-shrink: 0;
          " 
        />
        
        <div style="
          width: 100%;
          display: flex;
          flex-direction: row; 
          gap: 1.2mm; 
          align-items: center; 
          justify-content: center;
          text-align: center;
          margin-bottom: ${elementGap}; 
          line-height: 1;
          flex-shrink: 0;
          white-space: nowrap;
        ">
          <!-- MRP -->
          ${finalConfig.includeMrp ? `
          <div style="
            font-size: ${priceFontSize}px; 
            font-weight: 800;
            text-align: center;
            white-space: nowrap;
            letter-spacing: -0.2px;
          ">
            MRP: ${formattedMrp}
          </div>
          ` : ''}

          <!-- Selling -->

          ${finalConfig.includeSellingPrice ? `
          <div style="
            font-size: ${priceFontSize}px; 
            font-weight: 800; 
            text-align: center;
            white-space: nowrap;
            letter-spacing: -0.2px;
          ">
            Price: ${formattedSellingPrice}
          </div>
          ` : ''}
        </div>

        <!-- Product Code -->
        ${(finalConfig.includeProductCode) ? `
          <div style="
            font-size: ${productCodeFontSize}px; 
            font-weight: 800;
            white-space: nowrap; 
            overflow: hidden; 
            text-overflow: ellipsis; 
            width: 100%;
            line-height: 1;
            flex-shrink: 0;
          ">
            ${barcodeData.productCode ?? ''}
          </div>
        ` : ''}
      </div>
    `;
  };

  // Render all barcodes in the grid
  const allBarcodesHTML = barcodes
    .map((barcode) => renderBarcode(barcode))
    .join("");

  return `<!DOCTYPE html>
    <html>
    <head>
      <style>
        @media print {
          @page {
            size: auto;
            margin: 0mm;
          }
          body { margin: 0; padding: 0; overflow: hidden; }
          .sticker-page { 
            page-break-after: always; 
          }
          .sticker-page:last-child {
            page-break-after: auto;
          }
        }
      </style>
    </head>
    <body style="margin:0; padding:0; overflow: hidden; font-family: Arial, sans-serif;">
      <div class="sticker-page" style="
        width: 100%; 
        display: grid;
        margin: 0 auto; 
        grid-template-columns: ${layout.gridTemplateColumns};
        column-gap: ${layout.columnGap};
        row-gap: ${layout.rowGap};
        padding: ${pagePadding};
        box-sizing: border-box;
        overflow: hidden;
      ">
        ${allBarcodesHTML}
      </div>
    </body>
    </html>`;
};